import {List} from 'immutable';
import {useCallback, useMemo} from 'react';

import {resource} from '@albert-io/json-api-framework/request/builder';
import {useFluxStore} from 'client/framework';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';

import {useRetryErroredInterestOnce} from '../hooks/useRetryErroredInterestOnce';

export interface ClassGoogleClassroomSync {
  isReady: boolean;
  /** The class is linked to a Google Classroom course — false → per-row "cannot sync" tooltip. */
  classroomConnectedToGoogleClassroom: boolean;
  /** True when the assignment already has a classroom-scoped Google Classroom coursework id. */
  isSynced: (assignmentId: string) => boolean;
}

const getClassroomGoogleClassroomQuery = (classroomId: string) =>
  resource('classroom_v1').mandarkEndpoint(['classrooms_v1', classroomId]);

// The class's assignments with their classroom-scoped settings, so getGoogleCourseworkIdFromSettings
// can read the per-class google_coursework_id (presence = already synced). Mirrors
// useClassLtiSync's assignments query, including its co-teacher visibility filter and page cap.
const getClassAssignmentsGoogleClassroomQuery = (classroomId: string) =>
  resource('assignments_v3')
    .mandarkEndpoint(['classrooms_v1', classroomId, 'assignments_v3'])
    .include('classrooms_v1')
    .filter({included: {classrooms_v1: {id: classroomId}}})
    .pageSize(250);

/**
 * Per-class Google Classroom sync context for the v2 assignments list, sourced from Mandark (the GQL
 * `assignmentsAsEducator` layer exposes no Google Classroom fields). Mirrors useClassLtiSync's shape,
 * including gating the heavier per-assignment fetch behind the class's connection state (LTI gates
 * the same fetch behind a school-level deployment flag; GC has no school-level equivalent, so the
 * class-level connection is the gate instead).
 */
export const useClassGoogleClassroomSync = (
  classroomId: string | undefined,
  enabled: boolean
): ClassGoogleClassroomSync => {
  const classroomQuery = useMemo(
    () => (enabled && classroomId ? getClassroomGoogleClassroomQuery(classroomId) : null),
    [enabled, classroomId]
  );
  useRetryErroredInterestOnce(classroomQuery);
  const {isReady: classroomReady, resources: classroomResources} = useMandarkQueries(
    classroomQuery ? [classroomQuery] : []
  );
  // A retrying errored interest reports populated while its data is still an empty stub — treat it
  // as absent (not connected) rather than crash on a missing model method.
  const rawClassroom = classroomResources[0] as ClassroomModelV1 | undefined;
  const classroom =
    typeof rawClassroom?.getGoogleCourseId === 'function' ? rawClassroom : undefined;
  const classroomConnectedToGoogleClassroom = Boolean(
    classroomReady && classroom?.getGoogleCourseId()
  );

  // Skip the heavier per-assignment fetch entirely unless the class is connected — most classes
  // won't be, and the cell doesn't need per-assignment sync status to render its "not connected" state.
  const assignmentsQuery = useMemo(
    () =>
      enabled && classroomId && classroomConnectedToGoogleClassroom
        ? getClassAssignmentsGoogleClassroomQuery(classroomId)
        : null,
    [enabled, classroomId, classroomConnectedToGoogleClassroom]
  );
  useRetryErroredInterestOnce(assignmentsQuery);
  // Synced status is read best-effort from cache; in-session syncs are reflected by
  // useGoogleClassroomSync's local state. A cross-session stale read can briefly show "Sync" until
  // refetch — acceptable for now.
  const {isReady: assignmentsReady, resources: assignmentsResources} = useMandarkQueries(
    assignmentsQuery ? [assignmentsQuery] : []
  );
  // A retrying errored interest reports populated while its data is still an empty stub.
  const rawAssignments = assignmentsResources[0];
  const assignments = List.isList(rawAssignments)
    ? (rawAssignments as List<AssignmentModelV3>)
    : undefined;

  const syncedIds = useMemo(() => {
    const set = new Set<string>();
    assignments?.forEach((assignment) => {
      if (assignment.getGoogleCourseworkIdFromSettings({classroomId})) {
        set.add(assignment.getId());
      }
    });
    return set;
  }, [assignments, classroomId]);

  // Stable per-syncedIds identity so consumers can key memos on it (a fresh closure per render
  // defeats their dep arrays).
  const isSynced = useCallback((assignmentId: string) => syncedIds.has(assignmentId), [syncedIds]);

  return {
    isReady:
      !enabled ||
      (classroomReady &&
        (!classroomConnectedToGoogleClassroom || (assignmentsReady && !!assignments))),
    classroomConnectedToGoogleClassroom,
    isSynced
  };
};

// Teacher's classrooms, to check whether any is linked to a Google Classroom course — drives the
// cross-class explanatory affordance. The masquerade ids key the endpoint so it resolves the
// impersonated teacher under masquerade, matching the rest of the dashboard.
const getTeacherClassroomsGoogleClassroomQuery = (teacherId: string, schoolId: string | null) =>
  resource('classrooms_v1')
    .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
    .filter({school_v5: schoolId})
    .pageSize(250);

export interface AnyClassGoogleClassroomSync {
  isReady: boolean;
  anyClassConnectedToGoogleClassroom: boolean;
}

/**
 * Whether any of the teacher's classes is linked to a Google Classroom course — gates the
 * cross-class affordance. `enabled` keeps it inert in the class-scoped view so it can be called
 * unconditionally.
 */
export const useAnyClassGoogleClassroomSync = (enabled: boolean): AnyClassGoogleClassroomSync => {
  // Bridged: the query is built during render, so the masquerade reads that key its endpoint must be
  // subscription-based — a bare render read is invisible to the compiled component's memoization.
  const teacherId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');
  const masqueradeSchoolId = useFluxStore(masqueradeStore, (s) => s.getSchoolId());
  const query = useMemo(
    () =>
      enabled ? getTeacherClassroomsGoogleClassroomQuery(teacherId, masqueradeSchoolId) : null,
    [enabled, teacherId, masqueradeSchoolId]
  );
  const {isReady, resources} = useMandarkQueries(query ? [query] : []);
  const classrooms = resources[0] as List<ClassroomModelV1> | undefined;

  return {
    isReady,
    anyClassConnectedToGoogleClassroom: Boolean(
      isReady && classrooms?.some((classroom) => classroom.getGoogleCourseId())
    )
  };
};
